import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Choice from '@components/choice/Choice'
import Tag from '@components/tag/Tag'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import Select from '@components/select/Select'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as ControleStore from '@store/controle/controle'
import * as SocieteStore from '@store/societe/societe'
import * as MetierStore from '@store/metier/metier'
import * as SiteStore from '@store/site/site'

import useBreakpoint from '@utils/useBreakpoints'
import { createUUID } from '@utils/uuid'

const Start = ({ onForm, onBack }: ControleFormProps) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const forms = useReducer(ControleStore.store, (s) => s.forms)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const site = useReducer(SiteStore.store, (s) => s.site!)

  const previous = React.useMemo(
    () => forms.filter((f) => f.siteId === site.id).sort((a, b) => b.creationDate!.localeCompare(a.creationDate!)),
    [forms, site]
  )

  const onStart = () => {
    const init: Partial<ControleForm> = {
      id: createUUID(),
      siteId: site.id,
      reponses: [],
      localisationIds: [],
      step: 'start',
      creationDate: new Date().toISOString(),
    }

    onForm(init)
  }

  const onDelete = (id: string) => {
    SimpleAlert.open({
      title: i18n.t('common.delete'),
      description: i18n.t('screens.controles.form.delete'),
      buttons: [
        {
          label: i18n.t('common.yes'),
          onClick: () => {
            ControleStore.actions.removeForm(id)
            ControleStore.actions.resetForm()

            SimpleAlert.close()
          },
        },
        { label: i18n.t('common.no'), onClick: () => SimpleAlert.close() },
      ],
    })
  }

  const renderItem = (item: Partial<ControleForm>) => {
    const societe = services.find((s) => s.id === item.societeId)
    const metier = metiers.find((m) => m.id === item.metierId)

    return (
      <Item key={item.id} activeOpacity={0.9} onPress={() => onForm(item)}>
        <ItemBloc>
          <ItemTitle>{i18n.t('screens.controles.form.start.complete')}</ItemTitle>
          {!!item.nature && <Nature>{i18n.t(`screens.controles.form.nature.${item.nature}`)}</Nature>}

          {bp !== 'phone' && (
            <Delete activeOpacity={0.9} onPress={() => onDelete(item.id || '')}>
              <Icon name="delete" size={24} color={theme.colors.primary} />
            </Delete>
          )}
        </ItemBloc>

        <Separator />

        <ItemBloc>
          {!!metier && <Info>{metier.libelle}</Info>}
          {!!societe && <Info>{societe.nameSociete}</Info>}
          {!!item.creationDate && (
            <Info>{i18n.t('screens.controles.form.start.date', { date: new Date(item.creationDate) })}</Info>
          )}
        </ItemBloc>
      </Item>
    )
  }

  if (bp === 'phone') {
    return (
      <ScrollView contentContainerStyle={{ padding: 15, flexGrow: 1 }}>
        <Group>
          <SubTitle>{i18n.t('screens.controles.form.start.new')}</SubTitle>
          <Select label={i18n.t('screens.controles.form.start.startNew')} onClick={onStart} icon="arrow" />
        </Group>

        <SubTitle>{i18n.t('screens.controles.form.start.finish')}</SubTitle>

        {previous.length > 0 ? (
          previous.map(renderItem)
        ) : (
          <MissingInfo>{i18n.t('screens.controles.form.start.noForms')}</MissingInfo>
        )}
      </ScrollView>
    )
  }

  return (
    <DualLine>
      <Column left>
        <Title>{i18n.t('screens.controles.form.start.title')}</Title>
      </Column>
      <Column>
        <Content>
          <Animators>
            <Group>
              <SubTitle>{i18n.t('screens.controles.form.start.new')}</SubTitle>
              <Choice
                data={[{ label: i18n.t('screens.controles.form.start.startNew'), value: 'new' }]}
                onItemSelected={onStart}
              />
            </Group>

            <Group>
              <SubTitle>{i18n.t('screens.controles.form.start.finish')}</SubTitle>
              {previous.length > 0 ? (
                <ScrollView>{previous.map(renderItem)}</ScrollView>
              ) : (
                <MissingInfo>{i18n.t('screens.controles.form.start.noForms')}</MissingInfo>
              )}
            </Group>
          </Animators>
        </Content>
      </Column>

      <TagContainer>
        <Tag
          activeColor
          focused
          item={{ label: i18n.t('screens.controles.form.quit'), value: 'tag' }}
          leftIcon="close"
          onItemSelected={onBack}
        />
      </TagContainer>
    </DualLine>
  )
}

export default Start

const DualLine = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Column = styled(ScrollView)<{ left?: boolean }>`
  flex: 1;
  background-color: ${(props) => (props.left ? props.theme.colors.cardSecondary : props.theme.colors.background)};
  padding: 25px;
  padding-top: 175px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
`

const Content = styled(View)`
  margin: 0 20px;
`

const Group = styled(View)`
  margin-bottom: ${(props) => (props.theme.breakpoint === 'phone' ? 25 : 45)}px;
`

const SubTitle = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Title1)};
  margin-bottom: 8px;
`

const TagContainer = styled(View)`
  position: absolute;
  top: 25px;
  left: 25px;
`

const Item = styled(TouchableOpacity)`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.secondaryActive};
  border-radius: 6px;
  margin-bottom: 10px;
  position: relative;
`

const ItemBloc = styled(View)`
  padding: 16px 24px;
  justify-content: center;
`

const ItemTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Nature = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 14px;
`

const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
`

const Delete = styled(TouchableOpacity)`
  position: absolute;
  right: 20px;
  padding: 10px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
